import {
    Box,
    Divider,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    styled,
    SvgIconProps,
    SvgIconTypeMap,
} from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import React, { Fragment } from "react";
import { Link, LinkProps, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../redux/hooks";
import { getSelected, hexToRgb } from "./utils";

interface StyledListItemProps extends LinkProps {
    selected: boolean;
    open: boolean;
}

const StyledListItem = styled(ListItemButton, {
    shouldForwardProp: (p) => !["selected"].includes(p.toString()),
})<StyledListItemProps>(({ selected, theme }) => ({
    marginBottom: 8,
    paddingTop: "10px",
    paddingLeft: "22px",
    marginLeft: "0px",
    paddingBottom: "12px",
    height: "48px",
    borderRadius: "0px 30px 30px 0px",
    color: theme.palette.primary.contrastText,
    overflow: "hidden",
    ".MuiSvgIcon-root": {
        color: theme.palette.primary.contrastText,
    },
    ".MuiListItemText-primary": {
        fontWeight: "bold",
        fontSize: "0.825rem",
    },
    "&:hover": {
        background: `rgba(${hexToRgb("#6F4EE8", "string")}, 0.2)`,
    },
    ...(selected && {
        // background: theme.palette.primary.dark,
        borderLeft: `solid 5px #6F4EE8`,
        // marginLeft: "-5px",
        paddingLeft: "17px",
        color: "white",
        ".MuiSvgIcon-root": {
            color: "white",
        },
    }),
}));

const StyledFlyoutListItem = styled(ListItemButton, {
    shouldForwardProp: (p) => !["selected", "open"].includes(p.toString()),
})<{
    selected: boolean;
    open: boolean;
}>(({ selected, theme }) => ({
    marginBottom: 8,
    paddingTop: "10px",
    paddingLeft: "22px",
    marginLeft: "0px",
    paddingBottom: "12px",
    height: "48px",
    borderRadius: "0px 30px 30px 0px",
    color: theme.palette.primary.contrastText,
    position: "relative",
    zIndex: 2,
    overflow: "hidden",
    ".MuiSvgIcon-root": {
        color: theme.palette.primary.contrastText,
    },
    ".MuiListItemText-primary": {
        fontWeight: "bold",
        fontSize: "0.825rem",
    },
    "&:hover": {
        background: `rgba(${hexToRgb("#6F4EE8", "string")}, 0.2)`,
    },
    ...(selected && {
        // background: theme.palette.primary.dark,
        borderLeft: `solid 5px #6F4EE8`,
        // marginLeft: "-5px",
        paddingLeft: "9px",
        color: "white",
        ".MuiSvgIcon-root": {
            color: "white",
        },
    }),
}));

const StyledNestedListItem = styled(ListItemButton, {
    shouldForwardProp: (p) => !["selected", "open"].includes(p.toString()),
})<StyledListItemProps>(({ selected, theme, open }) => ({
    marginBottom: 0,
    paddingTop: "10px",
    paddingBottom: "12px",
    paddingLeft: open ? "44px" : "22px",
    marginLeft: "0px",
    height: "48px",
    color: theme.palette.primary.contrastText,
    transition: open ? "0s" : "0.2s",
    // background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "0px 30px 30px 0px",
    overflow: "hidden",
    ".MuiSvgIcon-root": {
        color: theme.palette.primary.contrastText,
    },
    ".MuiListItemText-primary": {
        fontWeight: "bold",
        fontSize: "0.825rem",
    },
    "&:hover": {
        background: `rgba(${hexToRgb("#6F4EE8", "string")}, 0.2)`,
    },
    ...(selected && {
        // background: theme.palette.primary.dark,
        borderLeft: `solid 5px #6F4EE8`,
        // marginLeft: "-5px",
        paddingLeft: open ? "39px" : "17px",
        color: "white",
        ".MuiSvgIcon-root": {
            color: "white",
        },
    }),
}));

const StyledFlyout = styled(Box, {
    shouldForwardProp: (p) => p !== "open",
})<{ open: boolean }>(({ theme, open }) => ({
    position: "absolute",
    top: 0,
    bottom: 0,
    ...(open
        ? {
              left: "100%",
          }
        : {
              right: 0,
          }),
    zIndex: 1,
    transition: "0.3s",
    borderRadius: "0px 30px 30px 0px",
    background: theme.palette.gradient?.main,
}));

export interface SidebarLinkProps {
    route?: string | string[];
    exact?: boolean;
    label: string;
    icon:
        | (OverridableComponent<SvgIconTypeMap<{}, "svg">> & {
              muiName: string;
          })
        | ((
              props: SvgIconProps & {
                  selected: boolean;
                  nested?: boolean;
              }
          ) => JSX.Element);
    hideIfLockedDown?: boolean;
    displayFlag?: string;
    requiredPython?: string;
    nested?: boolean;
    flyout?: boolean;
    flyoutContent?: JSX.Element;
}

const SidebarLink = ({
    route,
    exact = true,
    label,
    icon: Icon,
    hideIfLockedDown,
    displayFlag,
    requiredPython,
    nested = false,
    flyout,
    flyoutContent,
}: SidebarLinkProps) => {
    const isLockedDown = useAppSelector(
        (state) => state.yardidata.isLockedDown
    );
    const { pythonIntegrations, flags } = useAppSelector(
        (state) => state.config
    );
    const { sidebarOpen } = useAppSelector((state) => state.common);

    const [flyoutOpen, setFlyoutOpen] = React.useState(false);

    const location = useLocation();

    if (hideIfLockedDown && isLockedDown) return null;

    if (displayFlag && displayFlag.length > 0 && flags[displayFlag] === false)
        return null;

    if (requiredPython && !pythonIntegrations.includes(requiredPython))
        return null;

    const innerDisplay = (isSelected: boolean) => {
        return (
            <Fragment>
                <ListItemIcon>
                    <Icon
                        color="primary"
                        fontSize="small"
                        selected={isSelected}
                        nested={nested}
                    />
                </ListItemIcon>
                <ListItemText primary={label} />
            </Fragment>
        );
    };

    if (!flyout && route) {
        const to = typeof route === "string" ? route : route[0];

        const selected = getSelected(route, exact, location.pathname);

        return nested ? (
            <Fragment>
                {/* <Divider /> */}
                <StyledNestedListItem
                    key={label}
                    component={Link}
                    to={to}
                    open={sidebarOpen}
                    selected={selected}
                >
                    {innerDisplay(selected)}
                </StyledNestedListItem>
            </Fragment>
        ) : (
            <StyledListItem
                key={label}
                component={Link}
                to={to}
                open={sidebarOpen}
                selected={selected}
            >
                {innerDisplay(selected)}
            </StyledListItem>
        );
    } else if (flyout && flyoutContent) {
        return (
            <StyledFlyoutListItem
                key={label}
                open={sidebarOpen}
                selected={flyoutOpen}
                onClick={() => setFlyoutOpen(!flyoutOpen)}
            >
                {innerDisplay(flyoutOpen)}
                <StyledFlyout open={flyoutOpen}>{flyoutContent}</StyledFlyout>
            </StyledFlyoutListItem>
        );
    } else {
        return null;
    }
};

export default SidebarLink;
